"use strict";
var __extends = (this && this.__extends) || function (d, b) {
    for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p];
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
};
var events_1 = require('events');
var glShader = require('gl-shader');
var vec3 = require('gl-matrix/src/gl-matrix/vec3');
var mat4 = require('gl-matrix/src/gl-matrix/mat4');
var types_1 = require('../types');
var createBox = require('geo-3d-box');
var GLGeometry = require('gl-geometry');
var Renderer = (function (_super) {
    __extends(Renderer, _super);
    function Renderer(gl, mesher, stitcher, effectManager) {
        var _this = this;
        _super.call(this);
        this.texturesReady = function (texture) {
            _this.texture = texture.bind(); // used in tileMap uniform
        };
        this.gl = gl;
        this.mesher = mesher;
        this.stitcher = stitcher;
        this.effectManager = effectManager;
        if (!this.stitcher)
            throw new Error('voxel-shader requires voxel-stitch plugin'); // for tileCount uniform and updateTexture event
        this.stitcher.on('updateTexture', this.texturesReady);
        var box = createBox();
        this.box = new GLGeometry(gl)
            .attr('position', box.positions)
            .faces(box.cells);
        this.boxModelMatrix = mat4.create();
        this.boxOffset = vec3.fromValues(0.5, 0.5, 0.5);
        this.boxPosition = vec3.create();
        this.shader = this.createAOShader();
        this.shader2 = this.createCustomModelShader();
        this.objectShader = this.createObjectShader();
        this.focusedShader = this.createFocusedVoxelShader();
        this.spriteShader = this.createSpriteShader();
        this.sprites = [];
        this.texturedObjects = [];
        gl.enable(gl.DEPTH_TEST);
        gl.enable(gl.CULL_FACE);
    }
    Renderer.prototype.renderObject = function (gl, shader, object, forceUpdateModelMatrix) {
        if (forceUpdateModelMatrix === void 0) { forceUpdateModelMatrix = false; }
        var updated = object.updateModelMatrix(forceUpdateModelMatrix);
        switch (object.shaderType) {
            case types_1.ShaderType.PLAIN: {
                var triangleVAO = object.vao;
                if (triangleVAO && triangleVAO.length !== 0) {
                    shader.uniforms.uModel = object._modelMatrix;
                    triangleVAO.bind();
                    gl.drawArrays(gl.TRIANGLES, 0, triangleVAO.length);
                    triangleVAO.unbind();
                }
                break;
            }
            case types_1.ShaderType.SPRITE: {
                this.sprites.push(object);
                break;
            }
            case types_1.ShaderType.TEXTURE_SOLID: {
                this.texturedObjects.push(object);
                break;
            }
            case types_1.ShaderType.TEXTURE_POROUS: {
                break;
            }
        }
        for (var _i = 0, _a = object.children; _i < _a.length; _i++) {
            var child = _a[_i];
            this.renderObject(gl, shader, child, updated);
        }
    };
    Renderer.prototype.onRender = function (camera, chunks, objects, sunlight, focusedVoxel) {
        var gl = this.gl;
        var _a = this, shader = _a.shader, shader2 = _a.shader2;
        var viewMatrix = camera.viewMatrix, projectionMatrix = camera.projectionMatrix;
        gl.disable(gl.BLEND);
        this.sprites.length = 0;
        this.texturedObjects.length = 0;
        var objectShader = this.objectShader;
        // Draw objects
        objectShader.bind();
        objectShader.attributes.attrib0.location = 0;
        objectShader.attributes.attrib1.location = 1;
        objectShader.attributes.attrib2.location = 2;
        objectShader.uniforms.uProjection = projectionMatrix;
        objectShader.uniforms.uView = viewMatrix;
        objectShader.uniforms.uLightDirection = sunlight;
        for (var _i = 0, objects_1 = objects; _i < objects_1.length; _i++) {
            var object = objects_1[_i];
            this.renderObject(gl, objectShader, object);
        }
        // Draw sprites
        if (this.sprites.length > 0 || this.effectManager.effects.length > 0) {
            this.spriteShader.bind();
            this.spriteShader.uniforms.uProjection = projectionMatrix;
            this.spriteShader.uniforms.uView = viewMatrix;
            if (this.texture)
                this.spriteShader.uniforms.tileMap = this.texture;
            for (var _b = 0, _c = this.sprites; _b < _c.length; _b++) {
                var sprite = _c[_b];
                this.spriteShader.uniforms.uModel = sprite.modelMatrix;
                sprite.vao.bind();
                gl.drawArrays(gl.TRIANGLES, 0, sprite.vao.length);
                sprite.vao.unbind();
            }
            for (var _d = 0, _e = this.effectManager.effects; _d < _e.length; _d++) {
                var effect = _e[_d];
                effect.object.updateModelMatrix();
                this.spriteShader.uniforms.uModel = effect.object.modelMatrix;
                var vao = effect.object.vao;
                vao.bind();
                gl.drawArrays(gl.TRIANGLES, 0, vao.length);
                vao.unbind();
            }
        }
        // Bind the shader
        // phase 1 - solid blocks
        if (!shader)
            throw new Error("voxel-shader render() called before gl-init, shader=" + this.shader);
        shader.bind();
        shader.attributes.attrib0.location = 0;
        shader.attributes.attrib1.location = 1;
        shader.uniforms.projection = projectionMatrix;
        shader.uniforms.view = viewMatrix;
        shader.uniforms.tileCount = this.stitcher.tileCount;
        if (this.texture)
            shader.uniforms.tileMap = this.texture; // if a texture is loaded
        for (var _f = 0, _g = this.texturedObjects; _f < _g.length; _f++) {
            var object = _g[_f];
            var triangleVAO = object.vao;
            if (triangleVAO && triangleVAO.length !== 0) {
                shader.uniforms.model = object._modelMatrix;
                triangleVAO.bind();
                gl.drawArrays(gl.TRIANGLES, 0, triangleVAO.length);
                triangleVAO.unbind();
            }
        }
        for (var _h = 0, chunks_1 = chunks; _h < chunks_1.length; _h++) {
            var chunk = chunks_1[_h];
            var mesh = chunk.getVao(this.mesher);
            var triangleVAO = mesh['surface'];
            if (triangleVAO && triangleVAO.length !== 0) {
                shader.uniforms.model = chunk.modelMatrix;
                triangleVAO.bind();
                gl.drawArrays(gl.TRIANGLES, 0, triangleVAO.length);
                triangleVAO.unbind();
            }
        }
        gl.enable(gl.BLEND);
        // phase 2 - "porous" blocks
        gl.disable(gl.CULL_FACE);
        gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA); // TODO: premult alpha? https://github.com/deathcap/voxel-stitch/issues/6
        shader2.bind();
        shader2.attributes.position.location = 0;
        shader2.attributes.uv.location = 1;
        shader2.uniforms.projection = projectionMatrix;
        shader2.uniforms.view = viewMatrix;
        if (this.texture)
            shader2.uniforms.texture = this.texture;
        for (var _j = 0, chunks_2 = chunks; _j < chunks_2.length; _j++) {
            var chunk = chunks_2[_j];
            var mesh = chunk.getVao(this.mesher);
            var blockMesh = mesh['porous'];
            if (blockMesh && blockMesh.length !== 0) {
                shader2.uniforms.model = chunk.modelMatrix;
                blockMesh.bind();
                blockMesh.draw(gl.TRIANGLES, blockMesh.length);
                blockMesh.unbind();
            }
        }
        gl.enable(gl.CULL_FACE);
        // Cursor
        if (focusedVoxel) {
            gl.blendFunc(gl.SRC_ALPHA, gl.ONE);
            gl.enable(gl.POLYGON_OFFSET_FILL);
            gl.polygonOffset(-2, 2);
            vec3.add(this.boxPosition, focusedVoxel, this.boxOffset);
            mat4.fromTranslation(this.boxModelMatrix, this.boxPosition);
            this.box.bind(this.focusedShader);
            this.focusedShader.uniforms.uProjection = projectionMatrix;
            this.focusedShader.uniforms.uView = viewMatrix;
            this.focusedShader.uniforms.uModel = this.boxModelMatrix;
            this.box.draw();
            this.box.unbind();
            gl.disable(gl.POLYGON_OFFSET_FILL);
        }
    };
    ;
    Renderer.prototype.createAOShader = function () {
        return glShader(this.gl, require('raw!glslify!../../shaders/ao.vert'), require('raw!glslify!../../shaders/ao.frag'));
    };
    ;
    Renderer.prototype.createCustomModelShader = function () {
        // TODO: refactor with voxel-decals, voxel-chunkborder?
        return glShader(this.gl, require('raw!glslify!../../shaders/custom.vert'), require('raw!glslify!../../shaders/custom.frag'));
    };
    ;
    Renderer.prototype.createObjectShader = function () {
        return glShader(this.gl, require('raw!glslify!../../shaders/object.vert'), require('raw!glslify!../../shaders/object.frag'));
    };
    Renderer.prototype.createFocusedVoxelShader = function () {
        return glShader(this.gl, require('raw!glslify!../../shaders/focused.vert'), require('raw!glslify!../../shaders/focused.frag'));
    };
    Renderer.prototype.createSpriteShader = function () {
        return glShader(this.gl, require('raw!glslify!../../shaders/sprite.vert'), require('raw!glslify!../../shaders/sprite.frag'));
    };
    return Renderer;
}(events_1.EventEmitter));
Object.defineProperty(exports, "__esModule", { value: true });
exports.default = Renderer;
//# sourceMappingURL=Renderer.js.map


/*****************
 ** WEBPACK FOOTER
 ** /Users/songseunggeun/voxeline/engine/lib/Renderer/Renderer.js
 ** module id = 915
 ** module chunks = 0
 **/